// Whether to offer the in-browser path-traced ("Ray Traced") drinkware slide on this platform, and which
// path-tracer backend to run there.
//
// The WebGPU backend runs wherever the browser has WebGPU. It converges about twice as fast as the WebGL
// tracer on a Mac (~10s vs ~19s), and its glass is see-through, where the WebGL tracer's is darkened (and
// opaque black in macOS Chrome). Without WebGPU, what matters is how the platform compiles the WebGL
// tracer's shaders:
//
// - Apple (macOS, iOS, iPadOS): every browser translates to Metal, which compiles the WebGL tracer's single
//   giant GLSL kernel quickly, so the WebGL tracer runs there (Safari before 26, older iPhones).
// - Everywhere else: the WebGL kernel goes through ANGLE → HLSL → D3D11 on Windows, a 10s+ SYNCHRONOUS
//   compile on the main thread (measured on a Win11 / RTX 4080 in Chrome and Firefox) that freezes the tab.
//   The WebGPU backend's compute kernels avoid it: ~0.15s worst frame in Chrome on the same box (with
//   pathtrace-backend-webgpu.js's warm-up), ~2.3s in Firefox, whose async pipeline creation still blocks.
//   So off Apple the slide needs WebGPU. Android is held back until it's been tried on phone GPUs.
//
// Where the slide isn't offered, the carousel still has the pre-rendered Blender still ("Render") and the
// real-time raster "3D" slide. `window.__ptForce = true|false` overrides the gate (set it in the console
// before opening a drinkware product), and `?tdmpt=webgpu|webgl` picks the backend for testing.

function urlBackend() {
  return typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tdmpt") : null;
}

export function isApplePlatform() {
  if (typeof navigator === "undefined") return false;
  // Chromium (Chrome/Edge/Brave) exposes a precise OS string; trust it when present.
  const uaData = navigator.userAgentData;
  if (uaData && typeof uaData.platform === "string") return uaData.platform === "macOS";
  // Safari / Firefox: fall back to UA + platform strings. Covers macOS, iOS, and iPadOS — which since
  // iPadOS 13 reports a desktop "Macintosh" UA, still Apple/Metal.
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(ua) || /^(Mac|iPhone|iPad|iPod)/i.test(platform);
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent || "") || navigator.userAgentData?.platform === "Android";
}

// The backend the viewer runs when it isn't told otherwise ("auto").
export function defaultPathTracerBackend() {
  return typeof navigator !== "undefined" && navigator.gpu ? "webgpu" : "webgl";
}

// Whether a failed WebGPU start may fall back to the WebGL tracer, rather than to the Blender still. Only
// where WebGL compiles quickly, or when a tester forced the slide on.
export function webglFallbackAllowed() {
  return isApplePlatform() || (typeof window !== "undefined" && window.__ptForce === true);
}

export function pathTracerSupported() {
  if (typeof window !== "undefined" && window.__ptForce !== undefined) return !!window.__ptForce;
  if (typeof navigator === "undefined") return false;
  if (isApplePlatform()) return true;
  if (!navigator.gpu) return false;
  return !isAndroid() || urlBackend() === "webgpu";
}
